"use client";

/**
 * Where this session came in, for the hub's one suggestion (backend
 * services/entry_suggest, GET /api/me/suggestion).
 *
 * The first pageview of a session (lib/tracking) records the landing
 * page's kind and the few params that say what the person is doing:
 * the sale-tax calculator or the tax-zones map, a mortgage calculation,
 * a residence page, a filter landing (/menziller, /evler), a listing,
 * the auctions. A page may add a detail later in the same session (the
 * calculator's price, the address's district).
 *
 * Kept twice: in sessionStorage for this tab (the usual path: land,
 * sign in, arrive on the hub in the same tab), and as ``entry.context``
 * / ``entry.detail`` tracking events, which the backend reads when the
 * tab has nothing (another tab, another device, a later day).
 *
 * A link we sent (the digest, Telegram) says where it came from and which
 * recommendation it was: ``?via=digest&rec=<request id>&pos=<n>``. The
 * landing records them on ``entry.context`` (``via``, ``rec``, ``pos``),
 * or as ``entry.via`` when the page says nothing else or the session was
 * already running (recommender plan 2.2).
 */
import { trackEvent } from "@/lib/tracking";
import { recFromSearch } from "@/lib/rec-client";
import { listingIdFromSlug } from "@/lib/seo/paths";

export type EntryKind = "sale_tax" | "tax_zone" | "mortgage" | "residence" | "landing" | "listing" | "auctions";

export interface EntryContext {
  kind: EntryKind;
  /** A residence cluster id, a listing id, a landing's path. */
  ref?: string;
  place?: string;
  price?: number;
  lat?: number;
  lng?: number;
  /** When it was recorded (ms). */
  at: number;
}

const KEY = "menzil.entry";
const SENT = "menzil.entry.sent";

/** The landing page's kind and params, or null for a page that says
 *  nothing about what the person is doing. */
export function classify(path: string, search: string): Omit<EntryContext, "at"> | null {
  const seg = path.split("?")[0].split("/").filter(Boolean);
  const sp = new URLSearchParams(search);
  const first = seg[0] ?? "";
  if (first === "calculate" && seg[1] === "sale-tax") return { kind: "sale_tax" };
  if (first === "calculate" && seg[1] === "mortgage") {
    const price = Number(sp.get("price"));
    return { kind: "mortgage", ...(price > 0 ? { price } : {}) };
  }
  if (first === "map" && sp.get("layer") === "zones") return { kind: "tax_zone" };
  if (first === "blog" && /tax-zones|satisi-vergisi/.test(seg[1] ?? "")) return { kind: "tax_zone" };
  if (first === "residence" && seg[1]) return { kind: "residence", ref: decodeURIComponent(seg[1]).slice(0, 80) };
  if ((first === "menziller" || first === "evler") && seg.length > 1) return { kind: "landing", ref: seg.join("/").slice(0, 200) };
  if (first === "elan" && seg[1]) {
    const id = listingIdFromSlug(seg[1]);
    return id ? { kind: "listing", ref: id } : null;
  }
  if (first === "l" && /^\d{1,10}$/.test(seg[1] ?? "")) return { kind: "listing", ref: `own:${seg[1]}` };
  if (first === "auctions") return { kind: "auctions" };
  return null;
}

function write(e: EntryContext | null) {
  try {
    if (e) sessionStorage.setItem(KEY, JSON.stringify(e));
    else sessionStorage.removeItem(KEY);
  } catch { /* private mode: the server copy stands in */ }
}

export function readEntry(): EntryContext | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const e = JSON.parse(raw) as EntryContext;
    // A week-old tab says nothing about today.
    return e && e.kind && Date.now() - e.at < 7 * 864e5 ? e : null;
  } catch {
    return null;
  }
}

const VIA_RE = /^[a-z0-9_-]{1,24}$/;

/** The link's own attribution (`via`, `rec`, `pos`), or null. */
export function viaOf(search: string): { via?: string; rec?: string; pos?: number } | null {
  let via: string | null = null;
  try {
    via = new URLSearchParams(search).get("via");
  } catch {
    return null;
  }
  const r = recFromSearch(search);
  const out = {
    ...(via && VIA_RE.test(via) ? { via } : {}),
    ...(r?.rec ? { rec: r.rec } : {}),
    ...(r?.rec && r.pos != null ? { pos: r.pos } : {}),
  };
  return Object.keys(out).length ? out : null;
}

/** A landing of a running session (lib/tracking) that a link of ours
 *  tagged: recorded as `entry.via` with the page it opened. */
export function noteVia(path: string, search: string): void {
  const v = viaOf(search);
  if (v) trackEvent("entry.via", { ...v, p: path.slice(0, 120) });
}

/** Called by lib/tracking on the first pageview of a session. */
export function captureEntry(path: string, search: string): void {
  const c = classify(path, search);
  // A new session that starts somewhere neutral (the hub itself, the
  // listings page) keeps the tab's earlier entry; one that starts on a
  // telling page replaces it.
  if (!c) {
    noteVia(path, search);
    return;
  }
  const e: EntryContext = { ...c, at: Date.now() };
  write(e);
  try { sessionStorage.removeItem(SENT); } catch { /* none */ }
  trackEvent("entry.context", { ...c, ...viaOf(search) });
}

/** A page adds what it learned (the calculator's price, the address's
 *  district) to the entry of its own kind. The tracking event goes once
 *  per value, so a person typing does not send a request per key. */
export function noteEntry(kind: EntryKind, detail: Partial<Pick<EntryContext, "place" | "price" | "lat" | "lng">>): void {
  const cur = readEntry();
  if (!cur || cur.kind !== kind) return;
  const clean = Object.fromEntries(Object.entries(detail).filter(([, v]) => v != null && v !== "")) as Partial<EntryContext>;
  if (!Object.keys(clean).length) return;
  write({ ...cur, ...clean });
  const sig = JSON.stringify(clean);
  try {
    if (sessionStorage.getItem(SENT) === sig) return;
    sessionStorage.setItem(SENT, sig);
  } catch { /* send anyway */ }
  trackEvent("entry.detail", { kind, ...clean });
}

/** Once answered, the tab's entry has said what it had to say. */
export function clearEntry(): void {
  write(null);
}
